import { observable, computed, makeObservable } from 'mobx';
import { getMarkets, getMarketTranslation, MatchType } from './markets';

export const SELECTIONS_LABELS = (
    getTranslation: (key: string, defaultText: string) => string
): Record<string, string> => ({
    S: getTranslation('market.score', 'Score'),
    H: getTranslation('market.home', 'Home'),
    T: getTranslation('market.tie', 'Tie'),
    D: getTranslation('market.draw', 'Draw'),
    A: getTranslation('market.away', 'Away'),
    O: getTranslation('market.over', 'Over'),
    U: getTranslation('market.under', 'Under'),
    Y: getTranslation('market.yes', 'Y'),
    N: getTranslation('market.no', 'N'),
    'unnamed-favourite': getTranslation('market.uf', 'UF'),
    'tennis:H': '1',
    'tennis:A': '2',
    'darts:H': '1',
    'darts:A': '2',
    'boxing:H': '1',
    'boxing:D': 'X',
    'boxing:A': '2',
    'mixedmartialarts:H': '1',
    'mixedmartialarts:A': '2',
    'americanfootball:H': '1',
    'americanfootball:A': '2',
    'basketball:H': '1',
    'basketball:A': '2',
});

export const FIGHT_SPORTS = new Set(['boxing', 'mixedmartialarts']);

export const isFightSport = (sportId: string): boolean => {
    return FIGHT_SPORTS.has(sportId);
};

/** Boxing Fight Result is 3-way (includes draw). */
export const BOXING_EVENT_ROW_SELECTION_IDS = ['H', 'D', 'A'];

/** MMA Fight Result is 2-way (no draw column). */
export const MMA_EVENT_ROW_SELECTION_IDS = ['H', 'A'];

const SPORTS_WITH_EXPANDED_SELECTION_LAYOUT = new Set(['boxing', 'mixedmartialarts', 'tennis']);

export const shouldUseExpandedSelectionLayout = (sportId: string): boolean => {
    return SPORTS_WITH_EXPANDED_SELECTION_LAYOUT.has(sportId);
};

type LastSelectionsType = Array<'H' | 'D' | 'A' | 'O' | 'U' | 'Y' | 'N' | 'H-Y' | 'D-Y' | 'A-Y' | 'H-N' | 'A-N'>;

const isTwoWayHomeAwayHeaders = (headers: Array<string>): boolean => {
    return (
        headers.includes('H') === true &&
        headers.includes('A') === true &&
        headers.includes('D') === false &&
        headers.includes('T') === false
    );
};

export const resolveUnifiedSelectionIds = (params: {
    sportId: string;
    filterTemplateId: string | null;
    filterSelectionIds: Array<string>;
    activeFilterSelectionIds: Array<string>;
    fallbackHeaders: Array<string>;
}): Array<string> => {
    if (params.sportId === 'boxing') {
        if (isTwoWayHomeAwayHeaders(params.fallbackHeaders)) {
            return MMA_EVENT_ROW_SELECTION_IDS;
        }
        return BOXING_EVENT_ROW_SELECTION_IDS;
    }

    if (params.sportId === 'golf') {
        return [];
    }

    if (params.sportId === 'mixedmartialarts') {
        return MMA_EVENT_ROW_SELECTION_IDS;
    }

    if (params.filterTemplateId !== null) {
        return params.filterSelectionIds.length > 0 ? params.filterSelectionIds : params.activeFilterSelectionIds;
    }

    return params.fallbackHeaders;
};

export const getLastSelections = (filters_market: string): LastSelectionsType => {
    const lastMarket = filters_market;

    if (lastMarket === 'match-winner' || lastMarket === 'fight-winner' || lastMarket === 'fight-outright') {
        return ['H', 'D', 'A'];
    }
    if (
        lastMarket === 'match-winner-2' ||
        lastMarket === 'fight-result-draw-no-bet' ||
        lastMarket === 'money-line' ||
        lastMarket === '1st-half-money-line' ||
        lastMarket === '1st-quarter-money-line' ||
        lastMarket === 'fight-result'
    ) {
        return ['H', 'A'];
    }
    if (lastMarket === 'total-sets') {
        return ['O', 'U'];
    }
    if (lastMarket === 'both-teams-to-score') {
        return ['Y', 'N'];
    }
    if (lastMarket === 'total-goals-over-under') {
        return ['O', 'U'];
    }
    if (lastMarket === 'draw-no-bet') {
        return ['H', 'A'];
    }
    if (lastMarket === '1st-half-winner') {
        return ['H', 'D', 'A'];
    }
    if (lastMarket === 'match-result-and-both-to-score') {
        return ['H-Y', 'D-Y', 'A-Y'];
    }
    if (lastMarket === 'match-handicap' || lastMarket === 'first-half-handicap') {
        return ['H', 'A'];
    }
    if (lastMarket === 'total-points-over-under' || lastMarket === 'first-half-points-over-under') {
        return ['O', 'U'];
    }
    if (lastMarket === 'point-spread' || lastMarket === 'two-way-handicap') {
        return ['H', 'A'];
    }
    if (lastMarket === 'handicap-games' || lastMarket === 'match-result') {
        return ['H', 'A'];
    }
    if (lastMarket === 'total-games-over-under' || lastMarket === 'total-sets-over-under') {
        return ['O', 'U'];
    }

    return [];
};

export const getInitialMarket = (sportId: string): MatchType | null => {
    if (sportId === 'football') {
        return 'match-winner';
    }
    if (sportId === 'tennis') {
        return 'match-result';
    }
    if (sportId === 'basketball' || sportId === 'americanfootball') {
        return 'money-line';
    }
    // Baseball uses fixed 6-pack columns — no market filter.
    return null;
};

export class UnifiedEventsListMarkets {
    @observable public filters_market: MatchType | null = null;

    public constructor(
        public readonly getTranslation: (
            key: string,
            defaultText: string,
            params?: Record<string, string | number>
        ) => string,
        private readonly sportId: string
    ) {
        makeObservable(this);
        this.filters_market = getInitialMarket(this.sportId);
    }

    public getMarketTemplateId = (marketId: MatchType | null): string | null => {
        if (marketId === null) {
            return null;
        }
        const sportMarket = getMarkets(this.getTranslation, this.sportId).find((market) => market.id === marketId);
        if (sportMarket !== undefined) {
            return sportMarket.template;
        }
        return getMarketTranslation(this.getTranslation)[marketId].template;
    };

    public getMarketLine = (marketId: MatchType | null): number | null => {
        if (marketId === null) {
            return null;
        }
        const sportMarket = getMarkets(this.getTranslation, this.sportId).find((market) => market.id === marketId);
        if (sportMarket !== undefined) {
            return sportMarket.line;
        }
        return getMarketTranslation(this.getTranslation)[marketId].line;
    };

    public getLastSelectionsFor = (marketId: MatchType | null): Array<string> => {
        if (marketId === null) {
            return [];
        }

        if (marketId === 'money-line') {
            return ['H', 'A'];
        }

        const marketTemplateId = this.getMarketTemplateId(marketId);
        if (marketTemplateId === null) {
            return [];
        }
        // Tennis match winner is 2-way (no draw).
        if (this.sportId === 'tennis' && (marketTemplateId === 'match-winner' || marketTemplateId === 'match-result')) {
            return ['H', 'A'];
        }
        return getLastSelections(marketTemplateId);
    };

    @computed public get marketTemplateId(): string | null {
        return this.getMarketTemplateId(this.filters_market);
    }

    @computed public get marketLine(): number | null {
        return this.getMarketLine(this.filters_market);
    }

    @computed.struct public get lastSelections(): Array<string> {
        return this.getLastSelectionsFor(this.filters_market);
    }

    @computed.struct public get lastSelectionsHeader(): Array<string> {
        return this.translateSelectionHeaders(this.lastSelections);
    }

    public translateSelectionHeaders = (headers: Array<string>): Array<string> => {
        const selectionsLabels = SELECTIONS_LABELS(this.getTranslation);
        return headers.map((x) => selectionsLabels[`${this.sportId}:${x}`] ?? selectionsLabels[x] ?? x);
    };
}
